/**
 * Admin Data Service — Mock data layer backed by real historical CSV exports
 *
 * This service provides all data for the admin dashboard features.
 * It uses real license holder data from historical CSV exports (Aug 2022 — Jul 2025).
 *
 * When the real API endpoints are built, swap the implementations here
 * without changing any component code.
 */

// Data is loaded from public/mock-data/ via fetch (not bundled into JS)
// This keeps the bundle small while still using real data

// ============================================================
// Types
// ============================================================

export interface LicenseHolder {
  owner: string;
  email: string;
  tier: string;
  payment: string;
  hubspotPayment: string;
  tnRep: string;
  dateCreated: string;
  users: number;
  questions: number;
  teamName: string;
  teamLevel: string;
  teamSetup: string;
  // Computed fields
  healthScore: number;
  healthLabel: "Healthy" | "At Risk" | "Critical";
  setupCount: number;
}

export interface SnapshotSummary {
  date: string;
  totalAccounts: number;
  paidAccounts: number;
  totalQuestions: number;
  totalUsers: number;
  topTiers: Record<string, number>;
}

export interface AccountSnapshot {
  date: string;
  questions: number;
  users: number;
  tier: string;
  payment: string;
  teamSetup: string;
}

export interface PlatformMetrics {
  totalAccounts: number;
  paidAccounts: number;
  totalQuestions: number;
  totalUsers: number;
  avgHealthScore: number;
  healthyCount: number;
  atRiskCount: number;
  criticalCount: number;
  revenueAtRisk: number;
  revenueAtRiskCount: number;
  newThisMonth: number;
}

export interface CommunicationTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  category: "onboarding" | "retention" | "sales" | "general";
  channel: "email" | "sms";
  lastEdited: string;
  charCount?: number;
}

export interface AutomatedSequence {
  id: string;
  name: string;
  status: "active" | "paused" | "draft";
  trigger: string;
  steps: SequenceStep[];
  enrolled: number;
  openRate: number | null;
}

export interface SequenceStep {
  day: string;
  channel: "email" | "sms";
  templateName: string;
}

export interface CommunicationHistoryItem {
  id: string;
  channel: "email" | "sms";
  subject: string;
  sentDate: string;
  status: "opened" | "delivered" | "not_opened" | "bounced";
  detail?: string;
}

export interface CustomerNote {
  id: string;
  text: string;
  author: string;
  createdAt: string;
}

// ============================================================
// Health Score Calculation
// ============================================================

function parseSetupPercent(setup: string): number {
  const match = setup.match(/(\d+)/);
  return match ? parseInt(match[1]) / 100 : 0;
}

function getSetupCount(setup: string): number {
  const pct = parseSetupPercent(setup);
  return Math.round(pct * 5);
}

function calculateHealthScore(holder: {
  questions: number;
  users: number;
  payment: string;
  teamSetup: string;
  dateCreated: string;
  tier: string;
}): { score: number; label: "Healthy" | "At Risk" | "Critical" } {
  let score = 0;

  // Setup completeness (0-30 points)
  const setupPct = parseSetupPercent(holder.teamSetup);
  score += Math.round(setupPct * 30);

  // Questions answered (0-30 points)
  const q = holder.questions;
  if (q > 50000) score += 30;
  else if (q > 10000) score += 25;
  else if (q > 2000) score += 20;
  else if (q > 500) score += 15;
  else if (q > 100) score += 10;
  else if (q > 10) score += 5;

  // Payment status (0-20 points)
  if (holder.payment.includes("Paid") && !holder.payment.includes("Unpaid")) {
    score += 20;
  } else if (
    holder.payment.includes("Trial") &&
    !holder.payment.includes("Expired")
  ) {
    score += 10;
  }

  // Users / engagement (0-20 points)
  const u = holder.users;
  if (u > 50) score += 20;
  else if (u > 20) score += 15;
  else if (u > 10) score += 10;
  else if (u > 3) score += 5;

  score = Math.min(100, Math.max(0, score));

  const label: "Healthy" | "At Risk" | "Critical" =
    score >= 70 ? "Healthy" : score >= 40 ? "At Risk" : "Critical";

  return { score, label };
}

// ============================================================
// Data Processing
// ============================================================

// Process raw JSON into typed LicenseHolder objects
function processHolders(
  raw: Array<{
    owner: string;
    email: string;
    tier: string;
    payment: string;
    hubspot_payment: string;
    tn_rep: string;
    date_created: string;
    users: number;
    questions: number;
    team_name: string;
    team_level: string;
    team_setup: string;
  }>
): LicenseHolder[] {
  return raw.map((r) => {
    const health = calculateHealthScore({
      questions: r.questions,
      users: r.users,
      payment: r.payment,
      teamSetup: r.team_setup,
      dateCreated: r.date_created,
      tier: r.tier,
    });
    return {
      owner: r.owner,
      email: r.email,
      tier: r.tier,
      payment: r.payment,
      hubspotPayment: r.hubspot_payment,
      tnRep: r.tn_rep,
      dateCreated: r.date_created,
      users: r.users,
      questions: r.questions,
      teamName: r.team_name || "-",
      teamLevel: r.team_level || "-",
      teamSetup: r.team_setup,
      healthScore: health.score,
      healthLabel: health.label,
      setupCount: getSetupCount(r.team_setup),
    };
  });
}

// ============================================================
// Async Data Loading (from public/mock-data/)
// ============================================================

let _holders: LicenseHolder[] | null = null;
let _metrics: PlatformMetrics | null = null;
let _snapshotSummaries: SnapshotSummary[] | null = null;
let _accountHistory: Record<string, any[]> | null = null;

async function fetchJson(path: string): Promise<any> {
  const resp = await fetch(path);
  if (!resp.ok) throw new Error(`Failed to fetch ${path}: ${resp.status}`);
  return resp.json();
}

async function loadHolders(): Promise<LicenseHolder[]> {
  if (_holders) return _holders;
  const raw = await fetchJson("/mock-data/license-holders-latest.json");
  _holders = processHolders(raw);
  return _holders;
}

async function loadSnapshots(): Promise<SnapshotSummary[]> {
  if (_snapshotSummaries) return _snapshotSummaries;
  const raw = await fetchJson("/mock-data/snapshot-summaries.json");
  _snapshotSummaries = raw.map((s: any) => ({
    date: s.date,
    totalAccounts: s.total_accounts,
    paidAccounts: s.paid_accounts,
    totalQuestions: s.total_questions,
    totalUsers: s.total_users,
    topTiers: s.top_tiers,
  }));
  return _snapshotSummaries!;
}

async function loadAccountHistory(): Promise<Record<string, any[]>> {
  if (_accountHistory) return _accountHistory;
  _accountHistory = await fetchJson("/mock-data/account-history.json");
  return _accountHistory!;
}

// Synchronous getter (returns cached data or empty array)
function getHolders(): LicenseHolder[] {
  return _holders || [];
}

// ============================================================
// Public API
// ============================================================

export const AdminDataService = {
  /**
   * Initialize — preload all data. Call once on admin page mount.
   */
  async initialize(): Promise<void> {
    await Promise.all([loadHolders(), loadSnapshots(), loadAccountHistory()]);
  },

  /**
   * Get all license holders from the most recent snapshot (Jul 2025)
   */
  getLicenseHolders(): LicenseHolder[] {
    return getHolders();
  },

  async getLicenseHoldersAsync(): Promise<LicenseHolder[]> {
    return loadHolders();
  },

  /**
   * Get a single license holder by email
   */
  getLicenseHolder(email: string): LicenseHolder | undefined {
    return getHolders().find(
      (h) => h.email.toLowerCase() === email.toLowerCase()
    );
  },

  /**
   * Get platform-wide metrics
   */
  getPlatformMetrics(): PlatformMetrics {
    if (_metrics) return _metrics;

    const holders = getHolders();
    const totalAccounts = holders.length;
    const paidAccounts = holders.filter(
      (h) => h.payment.includes("Paid") && !h.payment.includes("Unpaid")
    ).length;
    const totalQuestions = holders.reduce((sum, h) => sum + h.questions, 0);
    const totalUsers = holders.reduce((sum, h) => sum + h.users, 0);

    const healthyCount = holders.filter((h) => h.healthLabel === "Healthy")
      .length;
    const atRiskCount = holders.filter((h) => h.healthLabel === "At Risk")
      .length;
    const criticalCount = holders.filter((h) => h.healthLabel === "Critical")
      .length;

    const avgHealthScore = Math.round(
      holders.reduce((sum, h) => sum + h.healthScore, 0) / totalAccounts
    );

    // Revenue at risk: unpaid accounts past 30 days that aren't free/trial
    const unpaidAlert = holders.filter((h) => h.payment === "Unpaid - Alert");
    const revenueAtRiskCount = unpaidAlert.length;
    // Rough estimate: $500 avg per unpaid account
    const revenueAtRisk = revenueAtRiskCount * 500;

    // New this month: accounts created in the latest month in the data
    const newThisMonth = holders.filter((h) => {
      const parts = h.dateCreated.split("/");
      if (parts.length >= 3) {
        const month = parseInt(parts[0]);
        const year = parseInt(parts[2]);
        // Latest data is Jul 2025
        return month === 7 && (year === 2025 || year === 25);
      }
      return false;
    }).length;

    _metrics = {
      totalAccounts,
      paidAccounts,
      totalQuestions,
      totalUsers,
      avgHealthScore,
      healthyCount,
      atRiskCount,
      criticalCount,
      revenueAtRisk,
      revenueAtRiskCount,
      newThisMonth,
    };

    return _metrics;
  },

  /**
   * Get historical snapshot summaries for platform analytics
   */
  getSnapshotSummaries(): SnapshotSummary[] {
    return _snapshotSummaries || [];
  },

  async getSnapshotSummariesAsync(): Promise<SnapshotSummary[]> {
    return loadSnapshots();
  },

  /**
   * Get historical data for a specific account (by email)
   */
  getAccountHistory(email: string): AccountSnapshot[] {
    const history = _accountHistory?.[email];
    if (!history) return [];
    return history.map((h: any) => ({
      date: h.date,
      questions: h.questions,
      users: h.users,
      tier: h.tier,
      payment: h.payment,
      teamSetup: h.team_setup,
    }));
  },

  /**
   * Get top N license holders by questions answered
   */
  getTopAccounts(limit = 20): LicenseHolder[] {
    return [...getHolders()]
      .sort((a, b) => b.questions - a.questions)
      .slice(0, limit);
  },

  /**
   * Get recently created accounts
   */
  getRecentSignups(limit = 20): LicenseHolder[] {
    return [...getHolders()]
      .sort((a, b) => {
        // Parse dates like "7/11/2025" or "7/11/25"
        const parseDate = (d: string) => {
          const parts = d.split("/");
          if (parts.length < 3) return 0;
          let year = parseInt(parts[2]);
          if (year < 100) year += 2000;
          return new Date(
            year,
            parseInt(parts[0]) - 1,
            parseInt(parts[1])
          ).getTime();
        };
        return parseDate(b.dateCreated) - parseDate(a.dateCreated);
      })
      .slice(0, limit);
  },

  /**
   * Get health score distribution (for histogram)
   */
  getHealthDistribution(): {
    range: string;
    count: number;
    category: string;
  }[] {
    const holders = getHolders();
    const ranges = [
      { range: "0-10", min: 0, max: 10 },
      { range: "11-20", min: 11, max: 20 },
      { range: "21-30", min: 21, max: 30 },
      { range: "31-40", min: 31, max: 40 },
      { range: "41-50", min: 41, max: 50 },
      { range: "51-60", min: 51, max: 60 },
      { range: "61-70", min: 61, max: 70 },
      { range: "71-80", min: 71, max: 80 },
      { range: "81-90", min: 81, max: 90 },
      { range: "91-100", min: 91, max: 100 },
    ];

    return ranges.map((r) => ({
      range: r.range,
      count: holders.filter(
        (h) => h.healthScore >= r.min && h.healthScore <= r.max
      ).length,
      category: r.max >= 70 ? "healthy" : r.max >= 40 ? "atRisk" : "critical",
    }));
  },

  /**
   * Get tier distribution
   */
  getTierDistribution(): { tier: string; count: number }[] {
    const holders = getHolders();
    const tierMap: Record<string, number> = {};
    holders.forEach((h) => {
      tierMap[h.tier] = (tierMap[h.tier] || 0) + 1;
    });
    return Object.entries(tierMap)
      .map(([tier, count]) => ({ tier, count }))
      .sort((a, b) => b.count - a.count);
  },

  // ============================================================
  // CRM Mock Data
  // ============================================================

  getEmailTemplates(): CommunicationTemplate[] {
    return [
      {
        id: "tmpl-1",
        name: "Welcome Email",
        subject: "Welcome to Team Nation Sports!",
        body:
          "Hi {{coach_name}},\n\nWelcome to Team Nation Sports! We're thrilled to have you and the {{team_name}} on board.\n\nHere's how to get started:\n1. Upload your playbook (or import from Hudl)\n2. Create your first flashcard game\n3. Invite your players\n\nYour team will be learning plays in minutes.\n\nBest,\nTeam Nation Sports",
        category: "onboarding",
        channel: "email",
        lastEdited: "Apr 1, 2026",
      },
      {
        id: "tmpl-2",
        name: "Onboarding Check-in (Day 7)",
        subject: "How's your first week going?",
        body:
          "Hi {{coach_name}},\n\nYou've been with Team Nation for a week now! Here's a quick look at how things are going:\n\n- Players invited: {{player_count}}\n- Plays in your playbook: {{play_count}}\n- Questions answered: {{questions_count}}\n\nNeed help with anything? Just reply to this email.\n\nBest,\nTeam Nation Sports",
        category: "onboarding",
        channel: "email",
        lastEdited: "Mar 20, 2026",
      },
      {
        id: "tmpl-3",
        name: "Onboarding Check-in (Day 30)",
        subject: "Your first month with Team Nation",
        body:
          "Hi {{coach_name}},\n\nYou've been using Team Nation for a month now. Here's your progress report:\n\n- Team setup: {{setup_progress}}\n- Total questions answered: {{questions_count}}\n- Most active player: {{top_player}}\n\nKeep it up!\n\nBest,\nTeam Nation Sports",
        category: "onboarding",
        channel: "email",
        lastEdited: "Mar 15, 2026",
      },
      {
        id: "tmpl-4",
        name: "Feature Announcement",
        subject: "New Feature: {{feature_name}}",
        body:
          "Hi {{coach_name}},\n\nWe're excited to announce {{feature_name}}! This new feature lets you {{feature_benefit}}.\n\nTry it now: Log in to your dashboard and {{feature_action}}.\n\nLet us know what you think!\n\nBest,\nTeam Nation Sports",
        category: "general",
        channel: "email",
        lastEdited: "Apr 8, 2026",
      },
      {
        id: "tmpl-5",
        name: "Monthly Usage Report",
        subject: "Your team's monthly report",
        body:
          "Hi {{coach_name}},\n\nHere's your {{team_name}} monthly report:\n\n- Active players: {{active_players}}\n- Questions answered this month: {{monthly_questions}}\n- Team readiness score: {{readiness_score}}\n- Top performer: {{top_player}}\n\nKeep up the great work!\n\nBest,\nTeam Nation Sports",
        category: "retention",
        channel: "email",
        lastEdited: "Apr 1, 2026",
      },
      {
        id: "tmpl-6",
        name: "Renewal Reminder (30 days)",
        subject: "Your subscription renews in 30 days",
        body:
          "Hi {{coach_name}},\n\nJust a friendly reminder that your {{tier}} subscription renews on {{renewal_date}}.\n\nYour team has answered {{questions_count}} questions — that's incredible engagement!\n\nIf you have any questions about your subscription, don't hesitate to reach out.\n\nBest,\nTeam Nation Sports",
        category: "retention",
        channel: "email",
        lastEdited: "Feb 1, 2026",
      },
      {
        id: "tmpl-7",
        name: "Renewal Reminder (7 days)",
        subject: "Renewing in 7 days",
        body:
          "Hi {{coach_name}},\n\nYour {{tier}} subscription renews in 7 days ({{renewal_date}}).\n\nNo action needed — your account will automatically continue. If you'd like to make changes, log in to your account settings.\n\nBest,\nTeam Nation Sports",
        category: "retention",
        channel: "email",
        lastEdited: "Feb 1, 2026",
      },
      {
        id: "tmpl-8",
        name: "Upgrade Offer",
        subject: "Unlock more with {{target_tier}}",
        body:
          "Hi {{coach_name}},\n\nYou're one of our most engaged {{tier}} coaches — your team has answered over {{questions_count}} questions!\n\nWith {{target_tier}}, you'd get:\n- Unlimited teams\n- Advanced analytics & exportable reports\n- Priority support\n- Custom branding\n\nReady to take the next step? Reply to this email or visit your account page.\n\nBest,\nTeam Nation Sports",
        category: "sales",
        channel: "email",
        lastEdited: "Mar 1, 2026",
      },
      {
        id: "tmpl-9",
        name: "Win Celebration",
        subject: "Congrats on the big win, Coach!",
        body:
          "Hi {{coach_name}},\n\nWe heard about {{team_name}}'s big win — congratulations! 🏈\n\nYour players' preparation is clearly paying off. {{team_name}} has answered {{questions_count}} questions this season.\n\nKeep the momentum going!\n\nBest,\nTeam Nation Sports",
        category: "retention",
        channel: "email",
        lastEdited: "Nov 15, 2025",
      },
      {
        id: "tmpl-10",
        name: "At-Risk Re-engagement",
        subject: "We miss you, Coach!",
        body:
          "Hi {{coach_name}},\n\nWe noticed it's been a while since your team used Team Nation. We'd love to help you get back on track!\n\nYour {{team_name}} has {{player_count}} players ready to go — here's what we suggest:\n\n1. Add a few more plays to your playbook\n2. Create flashcard games from those plays\n3. Your players can start practicing right away\n\nNeed help? Reply to this email or schedule a call with us.\n\nBest,\nTeam Nation Sports",
        category: "retention",
        channel: "email",
        lastEdited: "Mar 10, 2026",
      },
    ];
  },

  getSmsTemplates(): CommunicationTemplate[] {
    return [
      {
        id: "sms-1",
        name: "Welcome SMS",
        subject: "",
        body:
          "Welcome to Team Nation, Coach {{coach_name}}! Your team is set up and ready to go. Log in at app.teamnationsports.com to get started.",
        category: "onboarding",
        channel: "sms",
        lastEdited: "Apr 1, 2026",
        charCount: 148,
      },
      {
        id: "sms-2",
        name: "Setup Reminder",
        subject: "",
        body:
          "Hi Coach {{coach_name}}, you still have {{remaining_steps}} setup steps remaining. Complete them at app.teamnationsports.com to unlock the full experience!",
        category: "onboarding",
        channel: "sms",
        lastEdited: "Mar 20, 2026",
        charCount: 155,
      },
      {
        id: "sms-3",
        name: "Game Day",
        subject: "",
        body:
          "Good luck tonight, Coach {{coach_name}}! Your {{team_name}} players answered {{weekly_questions}} questions this week. They're ready! 🏈",
        category: "retention",
        channel: "sms",
        lastEdited: "Sep 1, 2025",
        charCount: 142,
      },
    ];
  },

  getSequences(): AutomatedSequence[] {
    return [
      {
        id: "seq-1",
        name: "New Account Onboarding",
        status: "active",
        trigger: "Account created",
        enrolled: 847,
        openRate: 72,
        steps: [
          { day: "Day 0", channel: "email", templateName: "Welcome Email" },
          { day: "Day 1", channel: "sms", templateName: "Setup Reminder" },
          {
            day: "Day 7",
            channel: "email",
            templateName: "Onboarding Check-in (Day 7)",
          },
          {
            day: "Day 14",
            channel: "email",
            templateName: "Feature Tips & Tricks",
          },
          {
            day: "Day 30",
            channel: "email",
            templateName: "First Month Progress Report",
          },
        ],
      },
      {
        id: "seq-2",
        name: "Renewal Reminder Series",
        status: "active",
        trigger: "60 days before expiration",
        enrolled: 234,
        openRate: 68,
        steps: [
          {
            day: "60 days before",
            channel: "email",
            templateName: "Renewal coming up",
          },
          {
            day: "30 days before",
            channel: "email",
            templateName: "Renewal Reminder (30 days)",
          },
          {
            day: "7 days before",
            channel: "email",
            templateName: "Renewal Reminder (7 days)",
          },
        ],
      },
      {
        id: "seq-3",
        name: "At-Risk Re-engagement",
        status: "active",
        trigger: "Health score drops below 40",
        enrolled: 156,
        openRate: 45,
        steps: [
          {
            day: "Day 0",
            channel: "email",
            templateName: "We miss you, Coach!",
          },
          { day: "Day 3", channel: "sms", templateName: "Quick check-in" },
          {
            day: "Day 7",
            channel: "email",
            templateName: "Here's what you're missing",
          },
          {
            day: "Day 14",
            channel: "email",
            templateName: "Special offer to come back",
          },
        ],
      },
      {
        id: "seq-4",
        name: "Win Streak Celebration",
        status: "paused",
        trigger: "MaxPreps reports 3+ game win streak",
        enrolled: 0,
        openRate: null,
        steps: [
          {
            day: "Immediate",
            channel: "email",
            templateName: "Win Celebration",
          },
        ],
      },
      {
        id: "seq-5",
        name: "Upgrade Path (Bronze → Silver)",
        status: "draft",
        trigger: "90 days on Bronze + health > 60",
        enrolled: 0,
        openRate: null,
        steps: [
          {
            day: "Day 0",
            channel: "email",
            templateName: "You've outgrown Bronze",
          },
          {
            day: "Day 7",
            channel: "email",
            templateName: "What Silver unlocks",
          },
          {
            day: "Day 14",
            channel: "email",
            templateName: "Limited-time upgrade offer",
          },
        ],
      },
    ];
  },

  /**
   * Get mock communication history for a customer
   */
  getCommunicationHistory(email: string): CommunicationHistoryItem[] {
    // Return plausible mock history based on account activity
    const holder = this.getLicenseHolder(email);
    if (!holder) return [];

    const isActive = holder.healthScore >= 40;
    const items: CommunicationHistoryItem[] = [];

    if (isActive) {
      items.push(
        {
          id: "comm-1",
          channel: "email",
          subject: "New Feature: AI Quiz Generator",
          sentDate: "Apr 8, 2026",
          status: "opened",
          detail: "Opened 3 times",
        },
        {
          id: "comm-2",
          channel: "email",
          subject: "Your Team's Monthly Report — March 2026",
          sentDate: "Apr 1, 2026",
          status: "opened",
          detail: "Clicked link to dashboard",
        },
        {
          id: "comm-3",
          channel: "sms",
          subject: `Hi Coach! Your players answered ${
            holder.questions > 100 ? Math.round(holder.questions / 52) : 12
          } questions this week.`,
          sentDate: "Mar 15, 2026",
          status: "delivered",
        },
        {
          id: "comm-4",
          channel: "email",
          subject: "Season Kickoff: New Features for Spring Practice",
          sentDate: "Feb 15, 2026",
          status: "opened",
        }
      );
    } else {
      items.push(
        {
          id: "comm-1",
          channel: "email",
          subject: "We miss you, Coach! Here's what's new...",
          sentDate: "Mar 10, 2026",
          status: "not_opened",
        },
        {
          id: "comm-2",
          channel: "email",
          subject: "Your Team's Monthly Report — February 2026",
          sentDate: "Mar 1, 2026",
          status: "not_opened",
        },
        {
          id: "comm-3",
          channel: "email",
          subject: "Season Kickoff: New Features for Spring Practice",
          sentDate: "Feb 15, 2026",
          status: "not_opened",
        }
      );
    }

    return items;
  },

  /**
   * Get mock notes for a customer
   */
  getCustomerNotes(email: string): CustomerNote[] {
    const holder = this.getLicenseHolder(email);
    if (!holder) return [];

    if (holder.healthScore >= 70 && holder.questions > 5000) {
      return [
        {
          id: "note-1",
          text: `Great engagement — ${holder.questions.toLocaleString()} questions answered. Strong candidate for case study/testimonial.`,
          author: holder.tnRep || "Admin",
          createdAt: "Mar 15, 2026",
        },
        {
          id: "note-2",
          text:
            "Onboarding went smoothly. Coach is tech-savvy and set up the playbook quickly.",
          author: holder.tnRep || "Admin",
          createdAt: holder.dateCreated,
        },
      ];
    } else if (holder.healthScore < 40) {
      return [
        {
          id: "note-1",
          text:
            "Attempted outreach twice — no response. Account appears inactive.",
          author: holder.tnRep || "Admin",
          createdAt: "Feb 20, 2026",
        },
      ];
    }

    return [];
  },
};

export default AdminDataService;
